/**
 * The Volta icon set.
 *
 * This is the ONLY file allowed to import from the `lucide-angular` icon barrel
 * (~1700 icons). Everything else renders icons through `<ui-icon name="...">`.
 * Keeping the barrel import in one place means:
 *   - the icons actually shipped are auditable in a single diff,
 *   - production builds tree-shake the unpicked icons,
 *   - dev rebuilds only parse the barrel once.
 *
 * Keys are Lucide's own PascalCase names, so a template may use either
 * `name="ShoppingBag"` or `name="shopping-bag"`. Look names up on lucide.dev.
 *
 * To add an icon: import it below and add it to VOLTA_ICONS. Nothing else.
 */
import {
  ArrowLeft,
  AtSign,
  Bell,
  Book,
  RefreshCw,
  Unlink,
  Unplug,
  X,
  Briefcase,
  Check,
  ChevronDown,
  Circle,
  ChevronRight,
  Clipboard,
  Code,
  Download,
  Eye,
  Gift,
  GitCompare,
  Globe,
  Info,
  LayoutGrid,
  Lightbulb,
  List,
  LogOut,
  Mail,
  MailOpen,
  MapPin,
  MessageSquare,
  Package,
  PanelLeft,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Send,
  Settings,
  Shield,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Split,
  Store,
  Tag,
  Target,
  Trash2,
  TriangleAlert,
  User,
  UserPlus,
  Users,
  UsersRound,
  Zap,
} from 'lucide-angular';

export const VOLTA_ICONS = {
  // ── Navigation: orders & inbox ──
  ShoppingBag, // Orders
  Mail, // Smart Inbox / e-mail accounts
  MessageSquare, // All conversations
  AtSign, // Mentions
  Pencil, // Drafts
  Send, // Sent

  // ── Navigation: products ──
  Package, // Inventory / Warehouses
  Download, // Import
  LayoutGrid, // Organize
  Clipboard, // Catalogs

  // ── Navigation: customers ──
  Store, // Companies
  List, // Company Groups
  Users, // Contacts / Users
  Target, // Segments

  // ── Navigation: account & capabilities ──
  Zap, // Automations / Workflows
  Book, // Knowledge
  Settings, // Settings
  Bell, // Notifications
  Briefcase, // Agents
  Gift, // Pricebook
  GitCompare, // Comparators

  // ── Settings ──
  User, // User profile
  UsersRound, // Teams
  Shield, // Roles & permissions
  ShieldCheck, // Two-step verification
  Code, // Custom fields
  Globe, // Language

  // ── Entities (detail panels) ──
  MapPin, // Shipping location
  Split, // Split / segregated orders

  // ── Row actions ──
  Trash2, // remove a row

  // ── Bulk actions (Smart Inbox) ──
  Tag, // change category
  Circle, // change status
  UserPlus, // assign to
  MailOpen, // mark as read (Mail above is the unread counterpart)

  // ── Affordances ──
  Info, // inline help tooltips
  Search, // page-header inline search
  TriangleAlert, // missing / unmatched data
  Eye, // preview a tip from its inventory row
  Lightbulb, // product tips & tours (WB-7535)
  Sparkles, // feature announcements (WB-7535)

  // ── Mailbox connections (Smart Inbox / Settings) ──
  RefreshCw, // refresh a connection
  Unlink, // disconnect a mailbox
  Unplug, // reconnect a dropped connection
  X, // dismiss a dialog, a coach-mark card or a panel

  // ── Controls ──
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  PanelLeft, // Sidebar collapse toggle
  LogOut,
  Plus,
  Check,
  RotateCcw, // reset a stored preference back to its default
};

/** Union of every icon name `<ui-icon>` accepts. */
export type VoltaIconName = keyof typeof VOLTA_ICONS;
